import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { revalidateLogic } from '@tanstack/react-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { maxPasswordLength } from '@/constants/numbers';
import {
  STYTCH_API_ERROR_TYPE,
  getStytchAPIErrorMessage,
  isStytchAPIError,
} from '@/error/StytchError';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import RateLimit from './RateLimitButton';
import SignInPanel from './SignInPanel';

type Props = {
  token: string;
};

const ResetPasswordDiscovery = ({ token }: Props) => {
  const navigate = useNavigate();
  const { pushNotification } = useNotification();
  const stytch = useStytchB2BClient();
  const formSchema = z.object({
    password: z
      .string()
      .min(8, {
        message: t`Password must be at least 8 characters`,
      })
      .regex(/[a-z]/, {
        message: t`Password must contain at least one lowercase letter`,
      })
      .regex(/[A-Z]/, {
        message: t`Password must contain at least one uppercase letter`,
      })
      .regex(/[0-9]/, {
        message: t`Password must contain at least one number`,
      })
      .regex(/[^a-zA-Z0-9]/, {
        message: t`Password must contain at least one special character (e.g. !, @, #, $, %, ^, &, *)`,
      }),
  });

  const form = useAppForm({
    defaultValues: {
      password: '',
    },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: formSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          const { intermediate_session_token, email_address, discovered_organizations } =
            await stytch.passwords.discovery.resetByEmail({
              password: value.password,
              password_reset_token: token,
            });

          // Navigate to discovery page with the discovered organizations
          navigate(ROUTES.signInDiscovery.path, {
            replace: true,
            state: {
              intermediateToken: intermediate_session_token,
              email: email_address,
              discoveredOrgs: discovered_organizations,
              isDiscoverySignUp: false,
              redirectUrl: null,
            },
          });
        } catch (e) {
          if (
            isStytchAPIError(e) &&
            e.error_type === STYTCH_API_ERROR_TYPE.UNABLE_TO_AUTH_MAGIC_LINK
          ) {
            // We want the error message to be a bit different for password resets
            e.error_type = 'unable_to_auth_reset_password';
          }
          pushNotification({
            type: 'error',
            message: getStytchAPIErrorMessage(e, ROUTES.signInPasswordForgot.path),
          });
        }
      },
    },
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <SignInPanel
        title={t`Reset your password`}
        footer={
          <form.AppForm>
            <RateLimit>
              <form.SubmitButton variant="primary" size="large" block>
                <Trans>Continue</Trans>
              </form.SubmitButton>
            </RateLimit>
          </form.AppForm>
        }
      >
        <form.AppField name="password">
          {(field) => (
            <field.Field>
              <FieldLabel htmlFor="reset-password-new-password">
                <Trans>New Password</Trans>
              </FieldLabel>
              <field.PasswordInput
                id="reset-password-new-password"
                autoComplete="new-password"
                maxLength={maxPasswordLength}
                autoFocus
                size="large"
              />
              <field.Error />
            </field.Field>
          )}
        </form.AppField>
      </SignInPanel>
    </form>
  );
};

export default ResetPasswordDiscovery;
